<template>
    <FrontContent :footer="footer">
        <div class="row justify-content-center">
            <div class="col-12 col-lg-8 col-xl-5">
                <div class="min-height-100vh has-header d-flex align-items-center">
                    <div class="form-content form-team-content w-100 white-content">
                        <div class="h1 mb-4 text-center text-uppercase">{{ $t('login.registro_titulo') }}</div>
                        <div class="thumb-container absolute" :style='"background-image: url(" + footer.logo + ")"'></div>
                        <ValidationObserver ref="observer" v-slot="{ invalid }">
                            <form class="w-100" @submit.prevent="register">
                                <div class="form-group">
                                    <label for="name">{{ $t('formularios.nombre') }}</label>
                                    <ValidationProvider name="name" rules="required" v-slot="{ errors }">
                                        <input type="text" name="name" id="name" v-model="formData.name" />
                                        <span class="error" v-if="errors[0]">{{ errors[0] }}</span>
                                        <span class="error" v-if="errores.name">{{ errores.name[0] }}</span>
                                    </ValidationProvider>
                                </div>

                                <div class="form-group">
                                    <label for="surnames">{{ $t('formularios.apellidos') }}</label>
                                    <ValidationProvider name="surnames" rules="required" v-slot="{ errors }">
                                        <input type="text" name="surnames" id="surnames" v-model="formData.surnames" />
                                        <span class="error" v-if="errors[0]">{{ errors[0] }}</span>
                                        <span class="error" v-if="errores.surnames">{{ errores.surnames[0] }}</span>
                                    </ValidationProvider>
                                </div>

                                <div class="form-group">
                                    <label for="email">{{ $t('formularios.email') }}</label>
                                    <ValidationProvider name="email" rules="required|email" v-slot="{ errors }">
                                        <input type="text" name="email" id="email" v-model="formData.email"/>
                                        <span class="error" v-if="errors[0]">{{ errors[0] }}</span>
                                        <span class="error" v-if="errores.email">{{ errores.email[0] }}</span>
                                    </ValidationProvider>
                                </div>

                                <div class="form-group">
                                    <label for="password">{{ $t('formularios.password') }}</label>
                                    <ValidationProvider name="password" rules="required|min:6" v-slot="{ errors }">
                                        <input type="password" name="password" id="password" v-model="formData.password"/>
                                        <span class="error" v-if="errors[0]">{{ errors[0] }}</span>
                                        <span class="error" v-if="errores.password">{{ errores.password[0] }}</span>
                                    </ValidationProvider>
                                </div>

                                <div class="form-group">
                                    <label for="password_confirmation">{{ $t('formularios.confirmar_password') }}</label>
                                    <ValidationProvider
                                        name="password_confirmation"
                                        rules="required|confirmed:password"
                                        v-slot="{ errors }"
                                    >
                                        <input type="password" name="password_confirmation" id="password_confirmation" v-model="formData.password_confirmation"/>
                                        <span class="error" v-if="errors[0]">{{ $t('formularios.passwords_no_coinciden') }}</span>
                                        <span class="error" v-if="errores.password_confirmation">{{ errores.password_confirmation[0] }}</span>
                                    </ValidationProvider>
                                </div>

                                <div class="form-group">
                                    <FormCaptchaComponent
                                        ref="captcha"
                                        :siteKey="sitekey"
                                        @success="onCaptchaVerified"
                                        @expired="invalidateCaptcha"
                                        @error="invalidateCaptcha"
                                    />
                                </div>

                                <div class="row justify-content-md-between">
                                    <div class="col-12 col-lg-6">
                                        <button v-if="!loading" type="submit" class="button" name="button" :disabled="invalid || !canSubmit">{{ $t('login.enviar') }}</button>
                                        <Skeleton v-else padding="tw:p-0" variant="">
                                            <template #skeleton>
                                                <SkeletonItem customClass="tw:w-full" height="tw:h-10" />
                                            </template>
                                        </Skeleton>
                                    </div>
                                    <div class="col-12">
                                        <p class="forgot recupera_texto">
                                            <router-link :to="{ name: 'login' }" class="text-underline text-bold">
                                                <small>{{ $t('login.vuelta_login') }}</small>
                                            </router-link>
                                        </p>
                                    </div>
                                </div>
                            </form>
                        </ValidationObserver>
                    </div>
                </div>
            </div>
        </div>
    </FrontContent>
</template>

<script setup>
    import { ref, computed, getCurrentInstance } from 'vue';
    import { useHead } from '@/composables/common/seo/useHead';
    import { useRegister } from '@/composables/auth/useRegister';
    import { Skeleton, SkeletonItem } from '@/modules/front/ui/skeleton';
    import FrontContent from '@/modules/front/layout/FrontContent';
    import FormCaptchaComponent from '@/modules/ui/FormCaptchaComponent.vue';

    useHead('Registrar');

    const instance = getCurrentInstance();
    const observer = ref(null);
    const captcha = ref(null);

    const {
        formData,
        errores,
        loading,
        sitekey,
        canSubmit,
        onCaptchaVerified,
        onCaptchaInvalid,
        submit
    } = useRegister();

    /**
     * The layout background is the view's concern, not the registration
     * composable's: a composable named for auth must not carry visual state, or
     * reusing it outside this layout drags the footer along. Same pattern as
     * FrontEnd.vue, which feeds the shared FooterComponent the same way.
     */
    const footer = computed(() => instance.proxy.$store.getters.getFooter);

    /**
     * The widget invalidated its token (expiry or hard failure) while the user
     * was still filling the form. The challenge must go back to a solvable
     * state, and the widget's own message explains why: reset and feedback are
     * separate concerns, so the message is kept.
     */
    const invalidateCaptcha = () => {
        onCaptchaInvalid();
        captcha.value?.reset({ keepFeedback: true });
    };

    /**
     * The view owns the widget ref, so it also owns resetting it after a
     * rejected submit. Turnstile tokens are single-use: without this the user
     * is left with a burned token and cannot retry.
     */
    const register = async() => {
        const valid = await observer.value.validate();
        if (!valid) return;

        const { ok } = await submit();

        if (ok) {
            instance.proxy.$store.commit('setFrontEvents', null);
            instance.proxy.$store.commit('setFrontExperiencias', null);
            instance.proxy.$toast.success(instance.proxy.$t('login.registro_exito'));

            if (window.location.href.includes('preventa')) {
                instance.proxy.$router.go(-1);
            } else {
                instance.proxy.$router.push({ name: 'perfil' });
            }
        } else {
            captcha.value?.reset();
            instance.proxy.$toast.error(instance.proxy.$t('login.registro_error'));
        }
    };
</script>

<style scoped>
    .recupera_texto {
        text-align: right;
    }

    @media (max-width: 768px)  {
        .recupera_texto {
        align-items: center;
        justify-content: center;
        text-align: center;
        margin: 1em;
        }
    }
</style>
